import React, { useEffect, useState } from "react";
import {
    IconButton,
    Modal,
    Box,
    TextField,
    Button,
    FormControl,
    Typography,
    Select,
    MenuItem,
    Grid,
    InputLabel,
    Alert,
    AlertTitle,
    CircularProgress,
    Paper,
    Fade,
    Slide,
    useTheme,
    useMediaQuery,
    Container,
    Divider,
    Stack,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
} from "@mui/material";
import CustomSelect from "../UI/CustomSelect";
import { API } from "aws-amplify";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import DownloadIcon from "@mui/icons-material/Download";
import {
    addUser,
    AdminCreateUserCognito,
    getDropdownData,
    getUsers,
} from "../Queries";
import * as XLSX from "xlsx";
import UserTable from "./UserTable";
import LoadingScreen from "../UI/LoadingScreen";

const api_name = process.env.REACT_APP_STAGE === "DEV" ? process.env.REACT_APP_API_NAME_DEV : process.env.REACT_APP_API_NAME_PROD;

const UserManagement = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Form styles
    const formStyles = {
        textField: {
            '& .MuiOutlinedInput-root': {
                fontFamily: 'Poppins',
                fontSize: '14px',
                borderRadius: '4px',
                backgroundColor: '#fff',
                height: '40px',
                '& fieldset': {
                    borderColor: '#E0E0E0',
                    borderRadius: '4px',
                },
                '&:hover fieldset': {
                    borderColor: '#9AA6AC'
                },
                '&.Mui-focused fieldset': {
                    borderColor: '#0a0068',
                    borderWidth: '1px'
                }
            },
            '& .MuiInputLabel-root': {
                fontFamily: 'Poppins',
                fontSize: '14px',
                color: '#666',
                transform: 'translate(14px, 12px)',
                '&.MuiInputLabel-shrink': {
                    backgroundColor: '#fff',
                    padding: '0 4px',
                    transform: 'translate(14px, -9px) scale(0.75)',
                }
            },
            '& .MuiInputBase-input': {
                fontFamily: 'Poppins',
                fontSize: '14px',
                padding: '8px 14px',
                height: '24px'
            }
        },
        select: {
            '& .MuiOutlinedInput-root': {
                height: '44px',
                borderRadius: '8px',
                backgroundColor: '#fff',
                transition: 'all 0.2s ease',
                '& fieldset': {
                    borderColor: '#E5E7EB',
                    borderRadius: '8px',
                },
                '&:hover fieldset': {
                    borderColor: '#2563EB'
                },
                '&.Mui-focused fieldset': {
                    borderColor: '#2563EB',
                    borderWidth: '1px'
                },
                '& .MuiSelect-icon': {
                    color: '#6B7280',
                    right: 12
                }
            },
            '& .MuiSelect-select': {
                fontFamily: 'Inter',
                fontSize: '14px',
                padding: '12px 16px !important',
                backgroundColor: '#fff',
                height: '20px !important',
                minHeight: 'unset !important',
                display: 'flex',
                alignItems: 'center',
                color: '#374151',
                letterSpacing: '0.2px'
            }
        },
        label: {
            fontFamily: 'Poppins',
            fontSize: '14px',
            color: '#666',
            fontWeight: 500,
            marginBottom: '8px'
        },
        modalTitle: {
            fontFamily: 'Poppins',
            fontSize: '18px',
            fontWeight: 600,
            color: '#333',
            marginBottom: '24px'
        }
    };

    const [registerData, setRegisterData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        dateOfBirth: "",
        qualification: "",
        programCode: [],
        role: "User",
        status: "Active",
        graduationYear: "",
        selectedCourses: [],
        designation: "",
        experience_level: "",
    });
    const [tableData, setTableData] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [editUser, setEditUser] = useState(false);
    const [error, setError] = useState(null);
    const [regCourses, setRegCourses] = useState([]);
    const [registrationCodes, setRegistrationCodes] = useState([]);
    const [alert, setAlert] = useState({
        open: false,
        severity: "success",
        message: "",
    });
    const [file, setFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const [programCodeMenuOpen, setProgramCodeMenuOpen] = useState(false);
    const [coursesMenuOpen, setCoursesMenuOpen] = useState(false);

    // Enhanced import states
    const [importModal, setImportModal] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [importResults, setImportResults] = useState({
        total: 0,
        successful: 0,
        failed: 0,
        errors: [],
        processing: false
    });
    const [previewData, setPreviewData] = useState([]);
    const [showPreview, setShowPreview] = useState(false);

    const handleCloseAlert = () => {
        setAlert({
            ...alert,
            open: false,
        });

        setRegisterData({
            username: "",
            email: "",
            pwd: "",
            firstName: "",
            lastName: "",
            dateOfBirth: "",
            phoneNumber: "",
            qualification: "",
            programCode: [],
            role: "",
            graduationYear: "",
            selectedCourses: [],
            designation: "",
            experience_level: "",
        });
        setOpen(false);
    };

    const handleSuccess = (message) => {
        setError(null);
        setAlert({
            open: true,
            severity: "success",
            message,
        });
        setLoading(false);
    };

    const handleError = (message) => {
        setError(message);
        setAlert({
            open: true,
            severity: "error",
            message,
        });
        setLoading(false);
    };

    const handleChange = (event) => {
        setRegisterData({
            ...registerData,
            [event.target.name]: event.target.value,
        });
    };

    const handleEdit = (data) => {
        setEditUser(true);
        setRegisterData({
            email: data.email,
            firstName: data.fname,
            lastName: data.lname,
            dateOfBirth: data.dob,
            pwd: data.password,
            phoneNumber: data.phone,
            qualification: data.qualification,
            programCode: Array.isArray(data.programCode) 
                ? data.programCode 
                : (data.programCode ? data.programCode.split(",") : []),
            role: data.role,
            status: data.status,
            graduationYear: Number(data.graduationYear),
            selectedCourses: Array.isArray(data.course) 
                ? data.course 
                : (data.course ? data.course.split(",") : []),
            designation: data.designation,
            experience_level: data.experience_level,
        });
        setOpen(true);
    };

    const handleDeleteConfirmation = (studentId) => {
        if (window.confirm("Are you sure you want to delete this user?")) {
            handleDelete(studentId);
        }
    };

    const handleDelete = (email) => {
        setOpen(true);
        setLoading(true);
        let apiName = api_name;
        let path = "";
        let params = {
            body: {
                command: "deleteUser",
                email: email,
            },
            response: true,
            convertEmptyValues: true,
            headers: {},
        };
        API.post(apiName, path, params)
            .then((response) => {
                if (response.data.statusCode === 200) {
                    let result = JSON.parse(response.data.body);
                    getTableData();
                    handleSuccess(result.message);
                } else {
                    handleError("Unable to delete the user. Please try again.");
                }
            })
            .catch((error) => {
                handleError("Unable to delete the user. Please try again.");
            });
    };

    const handleCourseChange = (event) => {
        setRegisterData({
            ...registerData,
            selectedCourses: event.target.value,
        });
    };

    const handleUser = async (event) => {
        event.preventDefault();
        setLoading(true);
        try {
            let response;
            if (editUser) {
                response = await addUser(registerData, editUser);
            } else {
                response = await AdminCreateUserCognito(registerData, editUser);
            }

            if (response.data && response.data.statusCode === 200) {
                let result = JSON.parse(response.data.body);
                getTableData();
                handleSuccess(result.message);
            } else {
                setLoading(false);
                handleError("Unable to add the user. Please try again.");
            }
        } catch (error) {
            setLoading(false);
            handleError("Unable to add the user. Please try again.");
        }
    };

    const handleOpenModal = () => {
        setRegisterData({
            username: "",
            email: "",
            pwd: "",
            firstName: "",
            lastName: "",
            dateOfBirth: "",
            phoneNumber: "",
            qualification: "",
            programCode: [],
            role: "",
            graduationYear: "",
            selectedCourses: [],
            designation: "",
            experience_level: "",
        });
        setEditUser(false);
        setOpen(true);
        setProgramCodeMenuOpen(false);
        setCoursesMenuOpen(false);
    };

    const handleCloseModal = () => {
        setOpen(false);
        setProgramCodeMenuOpen(false);
        setCoursesMenuOpen(false);
    };

    const getTableData = async () => {
        try {
            const users = await getUsers();
            setTableData(users);
            setIsLoading(false);
        } catch (error) {
            console.log('An error occurred while fetching users.', error);
        }
    };

    const getDropDowns = async () => {
        try {
            const dropdownData = await getDropdownData();

            const result1 = dropdownData.filter(
                (item) => item.type === "program_code"
            );
            const result2 = dropdownData.filter(
                (item) => item.type === "registration_course"
            );

            setRegistrationCodes(result1);
            setRegCourses(result2);
        } catch (error) {
            console.log('An error occurred while fetching dropdown data.', error);
        }
    };

    // Download template function
    const handleDownloadTemplate = async () => {
        setDownloadingTemplate(true);
        try {
            // Option 1: Direct S3 public URL
            const templateUrl = 'https://ts-file-storage.s3.us-east-1.amazonaws.com/resources/user_template.xlsx';
            window.open(templateUrl, '_blank');

        } catch (error) {
            console.error('Error downloading template:', error);
            handleError('Error downloading template. Please try again.');
        } finally {
            setDownloadingTemplate(false);
        }
    };

    // Validate Excel data structure
    const validateExcelData = (data) => {
        const requiredFields = ['first_name', 'last_name', 'email', 'client_code'];
        const errors = [];

        data.forEach((row, index) => {
            const missingFields = requiredFields.filter(field => !row[field] || row[field].toString().trim() === '');
            if (missingFields.length > 0) {
                errors.push(`Row ${index + 2}: Missing required fields: ${missingFields.join(', ')}`);
            }

            // Validate email format
            if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
                errors.push(`Row ${index + 2}: Invalid email format: ${row.email}`);
            }
        });

        return errors;
    };

    // Enhanced file handling
    const handleFileChange = async (event) => {
        const selectedFile = event.target.files[0];
        if (!selectedFile) return;

        setFile(selectedFile);
        setImporting(true);
        setImportResults({
            total: 0,
            successful: 0,
            failed: 0,
            errors: [],
            processing: false
        });

        try {
            const fileReader = new FileReader();
            fileReader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });
                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(sheet);

                    if (jsonData.length === 0) {
                        handleError("The Excel file is empty or has no valid data.");
                        setImporting(false);
                        return;
                    }

                    // Validate data structure
                    const validationErrors = validateExcelData(jsonData);
                    if (validationErrors.length > 0) {
                        setImportResults(prev => ({
                            ...prev,
                            errors: validationErrors,
                            total: jsonData.length
                        }));
                        setImportModal(true);
                        setImporting(false);
                        return;
                    }

                    // Set preview data
                    setPreviewData(jsonData.slice(0, 5)); // Show first 5 rows as preview
                    setImportResults(prev => ({
                        ...prev,
                        total: jsonData.length
                    }));
                    setShowPreview(true);
                    setImportModal(true);
                    setImporting(false);

                } catch (parseError) {
                    console.error("Error parsing Excel file:", parseError);
                    handleError("Error reading Excel file. Please check the file format.");
                    setImporting(false);
                }
            };
            fileReader.readAsArrayBuffer(selectedFile);
        } catch (error) {
            console.error("Error processing file:", error);
            handleError("Error processing file. Please try again.");
            setImporting(false);
        }

        // Clear the file input
        event.target.value = null;
    };

    // Process the import after confirmation
    const processImport = async () => {
        if (!file) return;

        setImportResults(prev => ({
            ...prev,
            processing: true,
            successful: 0,
            failed: 0,
            errors: []
        }));
        setShowPreview(false);
        setImportProgress(0);

        try {
            const fileReader = new FileReader();
            fileReader.onload = async (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(sheet);

                const errors = [];
                let successful = 0;
                let failed = 0;

                for (let i = 0; i < jsonData.length; i++) {
                    const user = jsonData[i];
                    const progress = ((i + 1) / jsonData.length) * 100;
                    setImportProgress(progress);

                    try {
                        const userData = {
                            firstName: user.first_name?.toString().trim() || '',
                            lastName: user.last_name?.toString().trim() || '',
                            email: user.email?.toString().trim().toLowerCase() || '',
                            pwd: user.password?.toString() || 'TempPassword123!',
                            phoneNumber: user.phone_number?.toString() || '',
                            dateOfBirth: user.date_of_birth?.toString() || '',
                            qualification: user.qualification?.toString() || '',
                            programCode: user.client_code?.toString().trim() 
                                ? user.client_code.toString().trim().split(',').map(code => code.trim()).filter(Boolean)
                                : [],
                            role: 'User',
                            status: 'Active',
                            graduationYear: user.graduation_year?.toString() || '',
                            selectedCourses: ['Corporate Registration'],
                            designation: user.designation?.toString() || '',
                            experience_level: user.experience_level?.toString() || '',
                        };

                        const response = await AdminCreateUserCognito(userData, false);

                        if (response.data && response.data.statusCode === 200) {
                            successful++;
                        } else {
                            failed++;
                            const errorMsg = response.data?.body ? JSON.parse(response.data.body)?.message : 'Unknown error';
                            errors.push(`Row ${i + 2} (${userData.email}): ${errorMsg}`);
                        }
                    } catch (error) {
                        failed++;
                        errors.push(`Row ${i + 2} (${user.email || 'Unknown'}): ${error.message || 'Processing error'}`);
                    }

                    // Small delay to prevent overwhelming the API
                    if (i < jsonData.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 100));
                    }
                }

                setImportResults(prev => ({
                    ...prev,
                    successful,
                    failed,
                    errors,
                    processing: false
                }));

                // Refresh the table data
                await getTableData();

                // Show completion message
                if (successful > 0) {
                    handleSuccess(`Import completed! ${successful} users added successfully${failed > 0 ? `, ${failed} failed` : ''}.`);
                } else if (failed > 0) {
                    handleError(`Import failed. ${failed} users could not be added.`);
                }
            };
            fileReader.readAsArrayBuffer(file);
        } catch (error) {
            console.error("Error during import:", error);
            setImportResults(prev => ({
                ...prev,
                processing: false
            }));
            handleError("Error during import process. Please try again.");
        }
    };

    const handleCloseImportModal = () => {
        setImportModal(false);
        setFile(null);
        setPreviewData([]);
        setShowPreview(false);
        setImportProgress(0);
        setImportResults({
            total: 0,
            successful: 0,
            failed: 0,
            errors: [],
            processing: false
        });
    };

    useEffect(() => {
        getDropDowns();
        setIsLoading(true);
        getTableData();
    }, []);

    return (
        <div style={{ height: '100%', background: 'white' }}>
            {/* Header Section */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                    <Typography
                        variant="h6"
                        component="h1"
                        gutterBottom={false}
                        sx={{ 
                            fontSize: '16px',
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            color: '#000',
                            // mb: 1
                        }}
                    >
                        User Management
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ 
                            fontFamily: 'Poppins',
                            color: '#9AA6AC',
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: 1.5,
                            fontFamily: 'Poppins',
                            fontWeight: 400
                        }}
                    >
                        Manage application users by adding, updating, or deleting accounts with ease. Assign users to specific roles and privileges to control access and functionality.
                    </Typography>
                </Box>

                <Divider />

                {/* Table Section with integrated controls */}
                <input
                    id="fileInput"
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                />

                <div
                    style={{
                        height: 'auto',
                        maxHeight: isMobile ? '70vh' : 'none',
                        width: "100%",
                        overflow: isMobile ? 'visible' : 'hidden',
                        background: 'white',
                        marginTop: "8px"
                    }}
                >
                    {!isLoading ? (
                        <UserTable
                            tableData={tableData}
                            handleEdit={handleEdit}
                            handleDelete={handleDeleteConfirmation}
                            isLoading={isLoading}
                            actionButtons={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Button
                                        variant="contained"
                                        onClick={handleOpenModal}
                                        startIcon={<PersonAddAltIcon sx={{ fontSize: 14 }} />}
                                        size="small"
                                        sx={{
                                            textTransform: "none",
                                            fontWeight: 500,
                                            borderRadius: 1,
                                            px: 2,
                                            fontSize: '13px',
                                            py: 0.75,
                                            fontSize: '0.8rem',
                                            background: "linear-gradient(45deg, #0a0068 30%, #cc4049 90%)",
                                            "&:hover": {
                                                background: "linear-gradient(45deg, #1565c0 30%, #0a0068 90%)",
                                            },
                                        }}
                                    >
                                        Add User
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleDownloadTemplate}
                                        startIcon={downloadingTemplate ? <CircularProgress size={12} /> : <DownloadIcon sx={{ fontSize: 16 }} />}
                                        disabled={downloadingTemplate}
                                        size="small"
                                        sx={{
                                            textTransform: "none",
                                            fontWeight: "bold",
                                            borderRadius: 2,
                                            px: 2,
                                            py: 0.75,
                                            fontSize: '0.8rem',
                                            borderColor: '#0a0068',
                                            color: '#0a0068',
                                            "&:hover": {
                                                backgroundColor: '#0a0068',
                                                color: "#fff"
                                            },
                                        }}
                                    >
                                        Template
                                    </Button>
                                    <label htmlFor="fileInput">
                                        <Button
                                            component="span"
                                            variant="outlined"
                                            startIcon={importing ? <CircularProgress size={12} /> : <CloudUploadIcon sx={{ fontSize: 16 }} />}
                                            disabled={importing}
                                            size="small"
                                            sx={{
                                                textTransform: "none",
                                                fontWeight: "bold",
                                                borderRadius: 2,
                                                px: 2,
                                                py: 0.75,
                                                fontSize: '0.8rem',
                                                borderColor: '#0a0068',
                                                color: '#0a0068',
                                                "&:hover": {
                                                    backgroundColor: '#0a0068',
                                                    color: "#fff"
                                                },
                                            }}
                                        >
                                            Import
                                        </Button>
                                    </label>
                                </Stack>
                            }
                        />
                    ) : (
                        <LoadingScreen message="Loading users..." />
                    )}
                </div>

            {/* Import Modal */}
            <Modal
                open={importModal}
                onClose={handleCloseImportModal}
                closeAfterTransition
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Fade in={importModal}>
                    <Paper
                        elevation={24}
                        sx={{
                            width: isSmallMobile ? '95vw' : isMobile ? '90vw' : '80vw',
                            maxWidth: 800,
                            maxHeight: isSmallMobile ? '95vh' : '90vh',
                            overflow: "auto",
                            borderRadius: 3,
                            p: 0,
                        }}
                    >
                        {/* Import Modal Header */}
                        <Box
                            sx={{
                                background: "linear-gradient(45deg, #0a0068 30%, #cc4049 90%)",
                                color: 'white',
                                p: 3,
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        fontSize: '16px',
                                        fontFamily: 'Poppins',
                                        fontWeight: 600,
                                        color: '#000'
                                    }}
                                >
                                    Import Users from Excel
                                </Typography>
                                <IconButton
                                    onClick={handleCloseImportModal}
                                    sx={{
                                        color: 'white',
                                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                    }}
                                    disabled={importResults.processing}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </Box>

                        {/* Import Modal Content */}
                        <Box sx={{ p: 2 }}>
                            {/* Processing State */}
                            {importResults.processing && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
                                        Processing Users... ({Math.round(importProgress)}%)
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={importProgress}
                                        sx={{ height: 8, borderRadius: 4, mb: 2 }}
                                    />
                                    <Typography variant="body2" color="text.secondary">
                                        Please don't close this window while importing users.
                                    </Typography>
                                </Box>
                            )}

                            {/* Results Display */}
                            {!importResults.processing && (importResults.successful > 0 || importResults.failed > 0) && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography 
                                        variant="h6" 
                                        gutterBottom 
                                        sx={{ 
                                            fontSize: '1rem',
                                            fontFamily: 'Poppins',
                                            fontWeight: 600
                                        }}
                                    >
                                        Import Results
                                    </Typography>
                                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                        <Chip
                                            icon={<CheckCircleIcon />}
                                            label={`${importResults.successful} Successful`}
                                            color="success"
                                            variant="outlined"
                                        />
                                        <Chip
                                            icon={<ErrorIcon />}
                                            label={`${importResults.failed} Failed`}
                                            color="error"
                                            variant="outlined"
                                        />
                                    </Stack>

                                    {importResults.errors.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" color="error" gutterBottom>
                                                Errors:
                                            </Typography>
                                            <Paper sx={{ maxHeight: 200, overflow: 'auto', p: 2, bgcolor: '#fafafa' }}>
                                                <List dense>
                                                    {importResults.errors.map((error, index) => (
                                                        <ListItem key={index} sx={{ py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                                <ErrorIcon color="error" fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={error}
                                                                primaryTypographyProps={{ variant: 'body2' }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Paper>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Preview Section */}
                            {showPreview && previewData.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
                                        Data Preview ({importResults.total} total records)
                                    </Typography>
                                    <Paper sx={{ p: 2, bgcolor: '#f8f9fa', mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            First 5 rows from your Excel file:
                                        </Typography>
                                        <Box sx={{ overflow: 'auto' }}>
                                            {previewData.map((row, index) => (
                                                <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1, fontSize: '0.875rem' }}>
                                                    <strong>Row {index + 2}:</strong> {row.first_name} {row.last_name} ({row.email}) - {row.client_code}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Paper>

                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <AlertTitle>Required Excel Columns</AlertTitle>
                                        Your Excel file should contain these columns: first_name, last_name, email, client_code.
                                        Optional columns: password, phone_number, date_of_birth, qualification, role, status, graduation_year, courses, designation, experience_level.
                                    </Alert>
                                </Box>
                            )}

                            {/* Validation Errors */}
                            {importResults.errors.length > 0 && showPreview && (
                                <Box sx={{ mb: 3 }}>
                                    <Alert severity="error">
                                        <AlertTitle>Validation Errors Found</AlertTitle>
                                        Please fix the following errors in your Excel file before importing:
                                        <List dense sx={{ mt: 1 }}>
                                            {importResults.errors.slice(0, 10).map((error, index) => (
                                                <ListItem key={index} sx={{ py: 0 }}>
                                                    <ListItemText
                                                        primary={error}
                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                    />
                                                </ListItem>
                                            ))}
                                            {importResults.errors.length > 10 && (
                                                <ListItem sx={{ py: 0 }}>
                                                    <ListItemText
                                                        primary={`... and ${importResults.errors.length - 10} more errors`}
                                                        primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                                                    />
                                                </ListItem>
                                            )}
                                        </List>
                                    </Alert>
                                </Box>
                            )}

                            {/* Action Buttons */}
                            <Stack
                                direction={isMobile ? "column" : "row"}
                                spacing={2}
                                justifyContent="flex-end"
                                sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={handleCloseImportModal}
                                    disabled={importResults.processing}
                                    size="large"
                                    fullWidth={isMobile}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: "bold",
                                        borderRadius: 2,
                                        px: 4,
                                        py: 1.5,
                                    }}
                                >
                                    {importResults.processing ? "Processing..." : "Cancel"}
                                </Button>

                                {showPreview && importResults.errors.length === 0 && (
                                    <Button
                                        variant="contained"
                                        onClick={processImport}
                                        disabled={importResults.processing}
                                        size="large"
                                        fullWidth={isMobile}
                                        sx={{
                                            textTransform: "none",
                                            fontWeight: "bold",
                                            borderRadius: 2,
                                            px: 4,
                                            py: 1.5,
                                            background: "linear-gradient(45deg, #0a0068 30%, #cc4049 90%)",
                                            "&:hover": {
                                                background: "linear-gradient(45deg, #1565c0 30%, #0a0068 90%)",
                                            },
                                        }}
                                    >
                                        Import {importResults.total} Users
                                    </Button>
                                )}
                            </Stack>
                        </Box>
                    </Paper>
                </Fade>
            </Modal>

            {/* Original User Modal */}
            <Modal
                open={open}
                onClose={handleCloseModal}
                closeAfterTransition
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Fade in={open}>
                    <Paper
                        elevation={24}
                        sx={{
                            width: isSmallMobile ? '95vw' : isMobile ? '90vw' : '80vw',
                            maxWidth: 700,
                            maxHeight: isSmallMobile ? '95vh' : '90vh',
                            overflow: "auto",
                            borderRadius: '12px',
                            p: 0,
                            backgroundColor: '#f8f9fa',
                            '& form': {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2
                            }
                        }}
                    >
                        {/* Modal Header */}
                        <Box
                            sx={{
                                background: "linear-gradient(45deg, #0a0068 30%, #cc4049 90%)",
                                color: 'white',
                                p: 3,
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
                                    {editUser ? "Edit User" : "Add New User"}
                                </Typography>
                                <IconButton
                                    onClick={handleCloseModal}
                                    sx={{
                                        color: 'white',
                                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </Box>

                        {/* Modal Content */}
                        <Box sx={{ p: 3 }}>
                            {loading && (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    flexDirection="column"
                                    gap={2}
                                    py={4}
                                >
                                    <CircularProgress 
                                        size={50} 
                                        sx={{
                                            '& .MuiCircularProgress-circle': {
                                                stroke: 'url(#loading-gradient)',
                                            },
                                        }}
                                    />
                                    <svg width={0} height={0}>
                                        <defs>
                                            <linearGradient id="loading-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                                <stop offset="0%" stopColor="#0a0068" />
                                                <stop offset="100%" stopColor="#cc4049" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                    <Typography 
                                        variant="body1" 
                                        color="text.secondary"
                                        sx={{
                                            fontSize: '0.9rem',
                                            fontWeight: 500
                                        }}
                                    >
                                        Processing...
                                    </Typography>
                                </Box>
                            )}

                            {alert.open && (
                                <Slide direction="down" in={alert.open}>
                                    <Alert
                                        severity={alert.severity}
                                        onClose={handleCloseAlert}
                                        sx={{ mb: 3, borderRadius: 2 }}
                                    >
                                        <AlertTitle sx={{ fontWeight: 'bold' }}>
                                            {alert.severity === "success" ? "Success" : "Error"}
                                        </AlertTitle>
                                        {alert.message}
                                    </Alert>
                                </Slide>
                            )}

                            {!loading && !alert.open && (
                                <Box component="form" onSubmit={handleUser} sx={{ width: '100%' }}>
                                    <Grid container spacing={2} sx={{ mt: 1.5 }}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="First Name"
                                                name="firstName"
                                                value={registerData.firstName}
                                                onChange={handleChange}
                                                required
                                                fullWidth
                                                variant="outlined"
                                                sx={formStyles.textField}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Last Name"
                                                name="lastName"
                                                value={registerData.lastName}
                                                onChange={handleChange}
                                                required
                                                fullWidth
                                                variant="outlined"
                                                sx={formStyles.textField}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                label="Email Address"
                                                type="email"
                                                name="email"
                                                value={registerData.email}
                                                onChange={handleChange}
                                                required
                                                fullWidth
                                                variant="outlined"
                                                sx={formStyles.textField}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <CustomSelect
                                                label="Company Code"
                                                name="programCode"
                                                value={registerData.programCode}
                                                onChange={handleChange}
                                                options={registrationCodes.map(item => ({
                                                    value: item.name,
                                                    label: item.name
                                                }))}
                                                multiple
                                                required
                                                placeholder="Select Company Code(s)"
                                                open={programCodeMenuOpen}
                                                onOpen={() => setProgramCodeMenuOpen(true)}
                                                onClose={() => setProgramCodeMenuOpen(false)}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <CustomSelect
                                                label="Registration Type"
                                                name="selectedCourses"
                                                value={registerData.selectedCourses}
                                                onChange={handleCourseChange}
                                                options={regCourses.map(item => ({
                                                    value: item.name,
                                                    label: item.name
                                                }))}
                                                multiple
                                                required
                                                placeholder="Select Registration Type(s)"
                                                open={coursesMenuOpen}
                                                onOpen={() => setCoursesMenuOpen(true)}
                                                onClose={() => setCoursesMenuOpen(false)}
                                            />
                                        </Grid>
                                                            {/* fontFamily: 'Inter',
                                                            fontSize: '14px',
                                            />
                                        </Grid> */}

                                        <Grid item xs={12} sm={6}>
                                            <CustomSelect
                                                label="Role"
                                                name="role"
                                                value={registerData.role}
                                                onChange={handleChange}
                                                required
                                                options={["User", "Client", "Mentor", "Manager", "Lead"]}
                                                placeholder="Select Role"
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Designation"
                                                name="designation"
                                                value={registerData.designation}
                                                onChange={handleChange}
                                                required
                                                fullWidth
                                                variant="outlined"
                                                sx={formStyles.textField}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <CustomSelect
                                                label="Experience Level"
                                                name="experience_level"
                                                value={registerData.experience_level}
                                                onChange={handleChange}
                                                required
                                                options={[
                                                    { value: "0-2", label: "0 - 2 Years" },
                                                    { value: "3-5", label: "3 - 5 Years" },
                                                    { value: "6+", label: "6+ Years" }
                                                ]}
                                                placeholder="Select Experience Level"
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <CustomSelect
                                                label="User Status"
                                                name="status"
                                                value={registerData.status}
                                                onChange={handleChange}
                                                required
                                                options={["Student", "Mentor", "Admin"]}
                                                placeholder="Select User Status"
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Action Buttons */}
                                    <Stack
                                        direction={isMobile ? "column" : "row"}
                                        spacing={2}
                                        justifyContent="flex-end"
                                        sx={{ 
                                            mt: 2, 
                                            pt: 2, 
                                            borderTop: '1px solid', 
                                            borderColor: '#E0E0E0',
                                            '& .MuiButton-root': {
                                                minWidth: isMobile ? '100%' : '140px',
                                                height: '36px',
                                                borderRadius: '8px'
                                            }
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            onClick={handleCloseModal}
                                            size="large"
                                            fullWidth={isMobile}
                                            sx={{
                                                textTransform: "none",
                                                fontWeight: "bold",
                                                borderRadius: 2,
                                                px: 4,
                                                py: 1.5,
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            fullWidth={isMobile}
                                            sx={{
                                                textTransform: "none",
                                                fontWeight: "bold",
                                                borderRadius: 2,
                                                px: 4,
                                                py: 1.5,
                                                background: "linear-gradient(45deg, #0a0068 30%, #cc4049 90%)",
                                                "&:hover": {
                                                    background: "linear-gradient(45deg, #1565c0 30%, #0a0068 90%)",
                                                },
                                            }}
                                        >
                                            {editUser ? "Update User" : "Create User"}
                                        </Button>
                                    </Stack>
                                </Box>
                            )}
                            {error && (
                                <Box sx={{ mt: 3 }}>
                                    <Alert severity="error">{error}</Alert>
                                </Box>
                            )}
                            </Box>
                    </Paper>
                </Fade>
            </Modal>
        </div>
    );
}

export default UserManagement;